import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslations } from 'src/contexts/TranslationContext';
import { formatCategoryName } from 'src/utils/categoryFormatter';
import { fetchNewsArchiveByMonth } from './NewsAPIUtil';
import {PageTitleDescriptionHelmet} from 'src/components/seo/NewsPageHelmets';
import { useCanonicalTag } from 'src/components/seo/useCanonicalTag';

interface NewsItem {
  id: string;
  title: string;
  postTime: number;
  url: string;
  subtype: string;
}

const ArchiveCategoryByMonth: React.FC = () => {
  const { category, yyyymm } = useParams<{ category: string; yyyymm: string }>();
  const { language, getUrlLanguagePrefix, translations: t } = useTranslations();

  const [news, setNews] = useState<NewsItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // adds or updates <link rel="canonical">
  useCanonicalTag();

  useEffect(() => {
    if (!category || !yyyymm) return;

    setLoading(true);
    setError(null);

    fetchNewsArchiveByMonth({
      lang: language || 'en',
      category,
      yyyymm,
      page: 0,
      size: 100
    })
      .then((data) => {
        const mappedNews = data.map((item) => ({
          id: item.id,
          title: item.title,
          postTime: item.postTime,
          url: `${getUrlLanguagePrefix(language)}/news/trendingnews/${item.id}`,
          subtype: item.subtype,

        }));
        setNews(mappedNews);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Unknown error');
        setLoading(false);
      });
  }, [category, yyyymm, language]);

  const formatMonthLabel = (yyyymmStr: string) => {
    if (yyyymmStr.length !== 6) return yyyymmStr;
    const year = parseInt(yyyymmStr.slice(0, 4), 10);
    const month = parseInt(yyyymmStr.slice(4, 6), 10);
    const date = new Date(year, month - 1);
    return date.toLocaleString(language || 'en', { year: 'numeric', month: 'long' });
  };

  if (!category || !yyyymm) {
    return <div>{t?.archive?.invalidParams || 'Invalid archive parameters.'}</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">

      <PageTitleDescriptionHelmet
        title={
          `${t?.navigations?.archive?.title || 'News Archive'} ` +
          `${t?.navigations?.header?.[category] || category} ${yyyymm}`
        }
        description={
          `${t?.navigations?.archive?.archivemonth?.metadescription || 'News Archive'} ` +
          `${t?.navigations?.header?.[category] || category} ${yyyymm}`
        }
      />

      <h1 className="text-3xl font-bold mb-4 text-center">
        {t?.navigations?.archive?.title || 'News Archive'} —{' '}
        {t?.navigations?.header?.[category] || formatCategoryName(category)}
        {/* {t?.navigations?.header?.[category] || formatCategoryName(category)} — {formatMonthLabel(yyyymm)} */}
      </h1>

      <h1 className="text-3xl font-bold mb-4 text-center">
        {/* {t?.navigations?.header?.archive || 'News Archive'} —{' '} */}
        {formatMonthLabel(yyyymm)}
      </h1>

      <div className="mb-6 text-left">
        <Link to={`${getUrlLanguagePrefix(language)}/news/archive/category/${category}`} className="text-blue-600 hover:underline text-lg sm:text-xl font-semibold">
          ← {t?.archive?.backToArchive || 'Back to Archive'}
        </Link>
      </div>

      {loading && <p>{t?.archive?.loading || 'Loading news...'}</p>}
      {error && <p className="text-red-600">{error}</p>}

      {!loading && !error && news.length === 0 && (
        <p>{t?.archive?.noNews || 'No news found for this category and month.'}</p>
      )}

      {!loading && !error && news.length > 0 && (
        <ul className="space-y-4">
          {news.map(({ id, title, postTime, url, subtype }) => (
            <li key={id} className="border-b pb-2">
              <article>
                <header>
                  <Link to={url} className="text-blue-700 hover:underline font-semibold">
                    {t?.navigations?.header?.[subtype?.toLowerCase?.()] || formatCategoryName(subtype)} -&nbsp;
                    <time dateTime={new Date(postTime * 1000).toISOString()}>
                      {new Date(postTime * 1000).toLocaleDateString(language || 'en')}
                    </time>
                  </Link>
                </header>

                <h2 className="text-lg font-semibold mt-1">
                  <Link to={url} className="text-blue-700 hover:underline">
                    {title}
                  </Link>
                </h2>
              </article>
            </li>
          ))}
        </ul>


      )}
    </div>
  );
};

export default ArchiveCategoryByMonth;
